/**
 * Findings API — typed client seam for the `recipe.findings.*` oRPC namespace
 * (B-WP13 v1). Mirrors `features/groups/api.ts`: reuses `createApiClient`
 * (credentials: include + CSRF double-submit) so every call rides the session
 * cookie, and defaults to same-origin (`baseUrl = ""`).
 *
 * Feature plan: .plan/features/2026-07-13-2027-q1-public-surfaces-cycle.md B-WP13.
 * Vocabulary (CLAUDE.md §5): Findings / Incorporate ONLY — never "pull
 * request" / "merge"; "fork" → spin-off. "diff" is NOT banned.
 */

import { createApiClient, type ApiClient } from "@proof/client-core/api";

// ── Wire types (mirror routes/recipes/findings.ts output shapes) ──────────────

export interface VariableRollupRow {
  label: string;
  changedCount: number;
  raisedCount: number;
  loweredCount: number;
}

export interface FindingsSpinoffSummary {
  spinoffUuid: string;
  /** Bare handle (no leading "@"); the UI prepends it. */
  ownerHandle: string;
  title: string;
  rating: number | null;
}

export interface FindingsAggregate {
  publicSpinoffCount: number;
  medianRating: number | null;
  variableRollup: VariableRollupRow[];
  spinoffs: FindingsSpinoffSummary[];
}

export interface FindingsDiffChange {
  label: string;
  kind: "changed" | "added" | "removed";
  before?: string;
  after?: string;
  direction: "raised" | "lowered" | null;
}

export interface FindingsContextChip {
  label: string;
  value: string;
}

export interface FindingsDrilldown {
  spinoffUuid: string;
  ownerHandle: string;
  title: string;
  rating: number | null;
  changes: FindingsDiffChange[];
  contextChips: FindingsContextChip[];
}

// ── API surface ───────────────────────────────────────────────────────────────

export interface FindingsApi {
  /** Creator-only aggregate over the recipe's PUBLIC lineage. */
  aggregate(recipeUuid: string): Promise<FindingsAggregate>;
  /** Drill into ONE public spin-off's diff + context chips. 404s a private uuid. */
  drilldown(recipeUuid: string, spinoffUuid: string): Promise<FindingsDrilldown>;
}

// ── Request-scoped aggregate dedupe (perf, open waterfall) ────────────────────
// MEASURED (open waterfall): `recipe/findings/aggregate` fired TWICE per recipe
// open, ~275 ms apart, the first still in flight when the second went out. Cause:
// the creator Cook footer's <FindingsPanel> can mount more than once in quick
// succession — ReadView is `key={activeRecipeUuid}` (remounts as the identity
// settles) and each mount's effect fires an aggregate() read. Because every
// FindingsPanel resolves its OWN api instance (`useState(() =>
// createFindingsApi())`), a per-instance closure cannot coalesce across mounts —
// so the in-flight map is MODULE-scoped and keyed by recipeUuid. A second call
// for the same recipe while the first is pending shares that promise; the entry
// clears the moment it settles, so a genuine later refetch still hits the wire.
// Different recipeUuids key separately and are never coalesced.
const aggregateInFlight = new Map<string, Promise<FindingsAggregate>>();

/**
 * Drop every in-flight aggregate promise. Call on sign-out: this map is module-
 * scoped and outlives the account, so without this an owner-A read still pending
 * when a different account signs in on the same tab could resolve into the new
 * account's UI (a seconds-wide cross-account leak window). Wired into the same
 * sign-out cache-reset path as the RPC / Yjs / snapshot purges.
 */
export function resetFindingsAggregateCache(): void {
  aggregateInFlight.clear();
}

function dedupedAggregate(client: ApiClient, recipeUuid: string): Promise<FindingsAggregate> {
  const existing = aggregateInFlight.get(recipeUuid);
  if (existing) return existing;
  const base = client.recipe.findings.aggregate({ recipeUuid });
  aggregateInFlight.set(recipeUuid, base);
  // Clear on settle (both branches), but only if we are still the current entry —
  // never evict a newer in-flight promise for the same recipe. Both handlers
  // return void so this cleanup branch is self-contained: it never re-throws and
  // cannot surface as an unhandled rejection (the caller keeps its own catch).
  const clear = () => {
    if (aggregateInFlight.get(recipeUuid) === base) aggregateInFlight.delete(recipeUuid);
  };
  void base.then(clear, clear);
  return base;
}

export function wrapFindingsApi(client: ApiClient): FindingsApi {
  return {
    aggregate: (recipeUuid) => dedupedAggregate(client, recipeUuid),
    drilldown: (recipeUuid, spinoffUuid) =>
      client.recipe.findings.drilldown({ recipeUuid, spinoffUuid }),
  };
}

/** Default factory used by the web surfaces. `baseUrl` defaults to same-origin. */
export function createFindingsApi(baseUrl = ""): FindingsApi {
  return wrapFindingsApi(createApiClient({ baseUrl }));
}
